import React, { ReactNode, useMemo, useState } from 'react';

import { Text } from '@rbilabs/universal-components';
import { GestureResponderEvent } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { t } from 'state/intl/translations';

import { SelectionsListItem, actionButtonStyles } from './styled';

interface SelectionsListProps {
  selections: { id: string; isDummyItem?: boolean; element: ReactNode }[];
  id: string;
  expandable: boolean;
}

// length of selections to show before truncating list
const MAX_SELECTIONS = 2;

const SelectionsList: React.FC<React.PropsWithChildren<SelectionsListProps>> = ({
  selections,
  expandable,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowExpandToggle = expandable && selections.length > MAX_SELECTIONS;
  const selectionsListLength = isExpanded ? selections.length : MAX_SELECTIONS;

  const selectionsListItem = useMemo(() => {
    return selections
      .slice(0, selectionsListLength)
      .map(({ id: selectionId, isDummyItem, element }) => {
        return (
          !isDummyItem && (
            <SelectionsListItem key={selectionId} testID={`modifiers-list-${selectionId}`}>
              {element}
            </SelectionsListItem>
          )
        );
      });
  }, [selections, selectionsListLength]);

  const remainingSelections = selections.length - selectionsListLength;
  const remainingSelectionsText =
    !shouldShowExpandToggle &&
    remainingSelections > 0 &&
    t('amountMore', { amountMore: remainingSelections });

  const handleClick = (event: GestureResponderEvent) => {
    if (expandable) {
      event.stopPropagation();
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      {selectionsListItem}
      {remainingSelectionsText && <Text variant="copyTwo">{remainingSelectionsText}</Text>}
      {shouldShowExpandToggle && (
        <ActionButton
          {...actionButtonStyles}
          size={ActionButtonSizes.SMALL}
          variant={ActionButtonVariants.TEXT_ONLY}
          onPress={handleClick}
        >
          {isExpanded ? t('showLess') : t('showMore')}
        </ActionButton>
      )}
    </>
  );
};

export default SelectionsList;
